import { useCallback, useState } from "react";

import { useQuery } from "@apollo/client";
import { Form, Radio, Select, RadioChangeEvent, RadioGroupProps, Input } from "antd";
import { NamePath } from "antd/lib/form/interface";

import { RVFormItem } from "@regal-voice/ui";
import { useFormInput } from "Hooks/useFormInput";
import { Team } from "Types/Team";

import { getTeamsForInputs } from "./queries";

const TEAM_RULES = [{ required: true, message: "Please select at least one team" }];

type TeamInputsProps = {
    title: string;
    showTeams: boolean;
    dropdownSize?: RadioGroupProps["size"];
    name?: NamePath;
    radioLabels?: Record<"all" | "specific", string>;
    description?: string;
    label?: string;
    teamOptionLabel?: string;
    optionsClassName?: string;
};

export function TeamsInputs({
    title,
    showTeams: initialShowTeams,
    dropdownSize,
    name = "teamIds",
    radioLabels = { all: "All Users", specific: "Specific Teams" },
    label = `${title} Visibility`,
    teamOptionLabel,
    description,
    optionsClassName,
}: TeamInputsProps) {
    const form = Form.useFormInstance();
    const { data, loading } = useQuery<{ getTeams: Array<Team> }>(getTeamsForInputs);

    const [showTeams, setShowTeams] = useState(initialShowTeams);

    const { getFieldName } = useFormInput();

    const onVisibilityChange = useCallback(
        (event: RadioChangeEvent) => {
            const shouldShowTeams = event.target.value;
            setShowTeams(shouldShowTeams);

            if (!shouldShowTeams) {
                form.setFieldValue(getFieldName(name), []);
            }
        },
        [form, setShowTeams, name, getFieldName]
    );

    return (
        <>
            {!showTeams && (
                <RVFormItem hidden name={name}>
                    <Input type="hidden" />
                </RVFormItem>
            )}
            <RVFormItem
                label={label}
                required={true}
                className={optionsClassName}
                description={
                    description ||
                    `Specify whether the ${title.toLowerCase()} will be enabled for all users or specific teams.`
                }
            >
                <Radio.Group
                    className="selection-radio"
                    size="small"
                    onChange={onVisibilityChange}
                    value={showTeams}
                    defaultValue={false}
                >
                    <Radio value={false} data-testid="teams-input-all-users">
                        {radioLabels.all}
                    </Radio>
                    <Radio value={true} data-testid="teams-input-specific-users">
                        {radioLabels.specific}
                    </Radio>
                </Radio.Group>
            </RVFormItem>

            {showTeams && (
                <RVFormItem
                    label="Team Selection"
                    required={true}
                    description={
                        teamOptionLabel || `Specify which teams this ${title.toLowerCase()} will be visible to.`
                    }
                    name={name}
                    rules={TEAM_RULES}
                >
                    <Select
                        size={dropdownSize}
                        mode="multiple"
                        showArrow={true}
                        placeholder="Select Teams"
                        optionFilterProp="label"
                        disabled={loading}
                        options={data?.getTeams?.map(({ id, name }) => ({
                            label: name,
                            value: id,
                        }))}
                        getPopupContainer={(trigger) => trigger.parentElement}
                        data-testid="teams-input-dropdown"
                    />
                </RVFormItem>
            )}
        </>
    );
}
